import { Button, Label, SelectProps, SemanticCOLORS } from 'semantic-ui-react'
import React, { ReactNode, useEffect, useState } from 'react'

import { DataSelect } from '../form/form'
import { Collection } from '../../types'
import TrEditButton from './trEditButton'
import TrCopyButton from './trCopyButton'
import { useApi } from '../../store/mainContext'

interface Props extends Omit<SelectProps, 'options'> {
  name: string
  label: string
  placeholder?: string
  url: string
  orderBy?(x: any): number
  dataCollection: Collection
  color?: SemanticCOLORS
  divider?: boolean
  onSuccess?(name: string, value?: any): void
  render?(x: any): ReactNode
}

const TrDataSelect = ({
  name,
  label,
  placeholder,
  value,
  onSuccess,
  onBlur,
  url,
  render: renderProp,
  dataCollection,
  multiple,
  search,
  divider,
  color,
  orderBy,
  ...other
}: Props) => {
  const render = React.useCallback(
    (x: any) => (typeof renderProp === 'function' ? renderProp(x) : x?.text),
    [renderProp],
  )

  const api = useApi()
  const [showEdit, setShowEdit] = useState(false)

  const [editMode, setEditMode] = useState(false)
  const [val, setVal] = useState(value)
  const [showAdd, setShowAdd] = useState(false)

  const [displayValue, setDisplayValue] = useState(render(value))

  const [isLoading, setIsLoading] = useState(false)

  const [, setCancelOnBlur] = useState(false)

  const refreshDisplayValue = React.useCallback(
    async (newvalue: any) => {
      if (!newvalue) {
        setDisplayValue(null)
        return
      }

      if (Array.isArray(newvalue)) {
        const { data } = await api.get(dataCollection)

        setDisplayValue(
          (newvalue || []).map((v) =>
            render((data.filter((d: any) => d._id === v) || [])[0] || {}),
          ),
        )

        return
      }

      const { data } = await api.get(`${dataCollection}/${newvalue}`)
      setDisplayValue(render(data))
    },
    [api, dataCollection, render],
  )

  useEffect(() => {
    refreshDisplayValue(value)
  }, [value, refreshDisplayValue])

  const handleSave = async () => {
    if (val === value) {
      handleCancel()
      return
    }

    setCancelOnBlur(false)
    setIsLoading(true)

    const obj: Record<string, any> = {}
    obj[name] = val

    if (!val || val === '' || val.length === 0) {
      await api.delete(`${url}/${name}`)
    } else {
      await api.patch(url, obj)
    }

    await refreshDisplayValue(val)

    setIsLoading(false)
    setEditMode(false)
    onSuccess && onSuccess(name, val)
  }

  const handleCancel = () => {
    setCancelOnBlur(false)
    setVal(value)
    setEditMode(false)
    setIsLoading(false)
  }

  const handleKeyPress = (e: KeyboardEvent) => {
    if (e.which === 13) handleSave()
  }

  if (editMode) {
    return (
      <tr>
        <th className={divider ? 'solidBorderTop' : ''}>{label}</th>
        <td
          className={divider ? 'solidBorderTop' : ''}
          style={{ paddingTop: 4, textAlign: `right` }}
        >
          <DataSelect
            {...other}
            name={name}
            disabled={isLoading}
            value={val}
            url={dataCollection}
            placeholder={placeholder}
            onKeyPress={handleKeyPress}
            onBlur={onBlur}
            onChange={(_: any, t: any) => setVal(t.value)}
            autoFocus
            style={{ width: '100%' }}
            multiple={multiple}
            search={search}
            render={render}
            orderBy={orderBy}
          />

          <div style={{ marginTop: 10 }}>
            <Button
              content="Cancel"
              size="tiny"
              onClick={handleCancel}
              style={{ padding: `9px 13px` }}
              disabled={isLoading}
              onMouseOver={() => setCancelOnBlur(true)}
              onMouseOut={() => setCancelOnBlur(false)}
            />

            <Button
              color="black"
              content="Save"
              size="tiny"
              style={{ padding: `9px 20px`, marginRight: 0 }}
              onClick={handleSave}
              loading={isLoading}
              onMouseOver={() => setCancelOnBlur(true)}
              onMouseOut={() => setCancelOnBlur(false)}
            />
          </div>
        </td>
      </tr>
    )
  }

  if (!val || val === '') {
    return (
      <tr
        onClick={() => setEditMode(true)}
        onMouseEnter={() => setShowAdd(true)}
        onMouseLeave={() => setShowAdd(false)}
        style={{ cursor: 'pointer' }}
      >
        <th>{label}</th>
        <td>
          {showAdd && (
            <span style={{ color: 'var(--primary)' }}>+ Add value</span>
          )}
        </td>
      </tr>
    )
  }

  if (Array.isArray(displayValue)) {
    return (
      <tr
        onMouseEnter={() => setShowEdit(true)}
        onMouseLeave={() => setShowEdit(false)}
      >
        <th>{label}</th>
        <td style={{ position: 'relative' }}>
          {displayValue.map((dv) => (
            <Label
              key={dv}
              color={color}
              style={{ margin: 0, marginBottom: 7, marginRight: 5 }}
            >
              {dv}
            </Label>
          ))}

          <TrCopyButton visible={showEdit} value={displayValue.join(', ')} />
          <TrEditButton onClick={() => setEditMode(true)} visible={showEdit} />
        </td>
      </tr>
    )
  }

  return (
    <tr
      onMouseEnter={() => setShowEdit(true)}
      onMouseLeave={() => setShowEdit(false)}
    >
      <th>{label}</th>
      <td style={{ position: 'relative' }}>
        {displayValue}
        <TrCopyButton visible={showEdit} value={displayValue} />
        <TrEditButton onClick={() => setEditMode(true)} visible={showEdit} />
      </td>
    </tr>
  )
}

export default TrDataSelect
